// Read-only organizer panel for an Oireachtas (Devin 2026-09-24). The organizer
// SEES their event — entries, teams, schools, their money, assessments — and
// changes nothing but their own "regional approval" marks on teams (a visual
// cue nothing else reads). The draw, check-in, scoring, publishing and every
// correction live in the admin panel. Data comes from the backend's organizer
// allowlist (router/oireachtas.js).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HeaderOrg from '../Headers/HeaderOrg';
import Loader from '../../Components/Loader/Loader';
import { formatDate } from '../../helper/formatDate';
import { closeTimeLabel } from '../../Teacher/Oireachtas/deadlines';
import { Msr, useMaterialSymbols } from '../../Teacher/Oireachtas/components';
import {
  download,
  getCompetitionList,
  getCompetitors,
  getOrganizerSummary,
  getReconciliation,
  getTeamRosters,
  getTransactions,
  setTeamApproval,
} from './api';
import { compareComps } from '../../Teacher/Oireachtas/compSort';
import { countSheetCsv, csvMoney, tableCsv } from './countSheet';
import OrganizerStatus from './OrganizerStatus';
import '../../Teacher/Oireachtas/oireachtas.css';
import './organizerOireachtas.css';

const TABS = [
  { key: 'overview', label: 'Overview', icon: 'dashboard' },
  // event day: where each competition is, read only (card f4270e09)
  { key: 'status', label: 'Status', icon: 'monitoring' },
  { key: 'competitions', label: 'Competitions', icon: 'format_list_numbered' },
  { key: 'competitors', label: 'Competitors', icon: 'groups' },
  { key: 'teams', label: 'Teams', icon: 'diversity_3' },
  { key: 'schools', label: 'Schools', icon: 'school' },
  { key: 'money', label: 'Money', icon: 'payments' },
  { key: 'assessments', label: 'Assessments', icon: 'request_quote' },
  { key: 'entries', label: 'Late & unfinished', icon: 'schedule' },
];

// Team rules a teacher can attest past with the regional director's approval
// (backend helpers/oireachtasTeamRules.js, overridable codes).
const RULE_LABELS = {
  TEAM_AGE_SKIP: 'Dancing two age groups up',
  TEAM_AGE_MIN_PROPER: 'Too few from the proper age group',
  TEAM_AGE_LOWER_MAX: 'Too many from a lower age group',
  TEAM_GENDER_MIX: 'Mixed-team male count',
  TEAM_REPEAT_HAND: 'Dancer on too many teams of this size',
  TEAM_REPEAT_LOWER: 'Too many repeating from a lower-age team',
};
const ruleLabel = (code) => RULE_LABELS[code] || code;
// stored dance keys ("four_hand_reel") -> "Four hand reel"
const danceLabel = (key) => {
  const t = String(key || '').replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '—';
};

// ---- formatting -------------------------------------------------------------
const money = (cents, currency) =>
  cents === null || cents === undefined
    ? '—'
    : new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(cents / 100);
// report endpoints return "123.45" dollar strings
const moneyStr = (s, currency) => money(Math.round(Number(s || 0) * 100), currency);
// stored calendar days (midnight UTC) render as that day
const day = (d) =>
  d ? formatDate(d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const when = (d, timeZone) =>
  d
    ? new Date(d).toLocaleString('en-CA', {
        timeZone: timeZone || undefined,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';
const REG_STATE = { open: 'Registration open', late: 'Late registration', closed: 'Registration closed' };

// ---- small building blocks (module level: never define components in render)
function Stat({ label, value, sub }) {
  return (
    <div className="op-stat">
      <div className="op-stat-v">{value}</div>
      <div className="op-stat-l">{label}</div>
      {sub ? <div className="op-stat-s">{sub}</div> : null}
    </div>
  );
}

function Panel({ title, actions, children }) {
  return (
    <section className="card op-panel">
      {(title || actions) && (
        <div className="op-panel-h">
          <div className="op-title" role="heading" aria-level="2">
            {title}
          </div>
          <div className="op-panel-a">{actions}</div>
        </div>
      )}
      {children}
    </section>
  );
}

// CSS-grid table (the .oir convention): cols = [{ key, label, w, align, render }]
// Columns with a `sort` accessor get a clickable header (asc -> desc -> asc).
function GridTable({ cols, rows, empty, rowKey, rowClass, defaultSort, footer, onRowClick }) {
  const [sort, setSort] = useState(defaultSort || null); // { key, dir }
  const template = cols.map((c) => c.w || '1fr').join(' ');
  const sorted = useMemo(() => {
    const col = sort && cols.find((c) => c.key === sort.key);
    if (!col || !col.sort || !rows) return rows || [];
    const val = (r) => col.sort(r);
    return [...rows].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const cmp =
        typeof x === 'number' && typeof y === 'number'
          ? x - y
          : String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
  }, [rows, sort, cols]);
  if (!rows || rows.length === 0) return <div className="op-empty">{empty || 'Nothing to show yet.'}</div>;
  const toggle = (c) =>
    setSort((cur) => (cur && cur.key === c.key ? { key: c.key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'asc' }));
  return (
    <div className="op-table" role="table">
      <div className="op-tr op-th" role="row" style={{ gridTemplateColumns: template }}>
        {cols.map((c) =>
          c.sort ? (
            <button
              key={c.key}
              type="button"
              role="columnheader"
              aria-sort={sort && sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              className={`op-sortable${c.align === 'right' ? ' r' : ''}${sort && sort.key === c.key ? ' on' : ''}`}
              onClick={() => toggle(c)}
            >
              {c.label}
              <Msr>{sort && sort.key === c.key ? (sort.dir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}</Msr>
            </button>
          ) : (
            <div key={c.key} role="columnheader" className={c.align === 'right' ? 'r' : ''}>
              {c.label}
            </div>
          )
        )}
      </div>
      {sorted.map((r, i) => (
        <div
          key={rowKey ? rowKey(r, i) : i}
          className={`op-tr${rowClass ? ` ${rowClass(r) || ''}` : ''}${onRowClick ? ' op-click' : ''}`}
          role="row"
          style={{ gridTemplateColumns: template }}
          {...(onRowClick
            ? {
                tabIndex: 0,
                onClick: () => onRowClick(r),
                onKeyDown: (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(r);
                  }
                },
              }
            : {})}
        >
          {cols.map((c) => (
            <div key={c.key} role="cell" className={c.align === 'right' ? 'r' : ''} data-label={c.label}>
              {c.render ? c.render(r) : r[c.key] ?? ''}
            </div>
          ))}
        </div>
      ))}
      {footer ? (
        <div className="op-tr op-tfoot" role="row" style={{ gridTemplateColumns: template }}>
          {cols.map((c) => (
            <div key={c.key} role="cell" className={c.align === 'right' ? 'r' : ''} data-label={c.label}>
              {footer[c.key] ?? ''}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// "a@x, b@y" (one school, several teachers) -> one mailto link each
function Emails({ value }) {
  const list = String(value || '')
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="op-muted op-small">
      {list.map((e, i) => (
        <React.Fragment key={e}>
          {i > 0 ? ', ' : null}
          <a href={`mailto:${e}`}>{e}</a>
        </React.Fragment>
      ))}
    </div>
  );
}

function DownloadBtn({ onClick, children }) {
  return (
    <button type="button" className="op-btn" onClick={onClick}>
      <Msr>download</Msr>
      {children}
    </button>
  );
}

function Search({ value, onChange, placeholder }) {
  return (
    <label className="op-search">
      <Msr>search</Msr>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}

// ---- tab bodies -------------------------------------------------------------
function Overview({ s }) {
  const cur = s.overview.currency;
  const fs = s.feeSchedule;
  const reg = s.overview.registration || {};
  return (
    <>
      <div className="op-stats">
        <Stat label="Schools" value={s.counts.schools} />
        <Stat label="Dancers" value={s.counts.dancers} />
        <Stat label="Solo entries" value={s.counts.soloEntries} />
        <Stat label="Teams" value={s.counts.teams} />
        <Stat label="Collected for your event" value={money(s.money.collectedCents, cur)} />
        <Stat label="Still owed (instalments)" value={money(s.money.outstandingCents, cur)} />
      </div>
      <div className="op-cols">
        <Panel title="Event dates">
          <dl className="op-dl">
            <dt>Registration</dt>
            <dd>
              <span className={`op-pill ${reg.state || ''}`}>{REG_STATE[reg.state] || '—'}</span>
              {reg.daysLeft !== null && reg.daysLeft !== undefined ? (
                <span className="op-muted"> {reg.daysLeft === 0 ? 'last day' : `${reg.daysLeft} days left`}</span>
              ) : null}
            </dd>
            <dt>Opens</dt>
            <dd>{day(s.overview.openingDate)}</dd>
            <dt>Closes</dt>
            <dd>
              {day(s.overview.entriesCloseDate)}{' '}
              <span className="op-muted">{closeTimeLabel(s.overview, s.overview.entriesCloseDate) || ''}</span>
            </dd>
            <dt>Late close</dt>
            <dd>
              {day(s.overview.lateEntryCloseDate)}{' '}
              <span className="op-muted">{closeTimeLabel(s.overview, s.overview.lateEntryCloseDate) || ''}</span>
            </dd>
            <dt>Event days</dt>
            <dd>{(s.overview.eventDays || []).map(day).join(' – ') || '—'}</dd>
            <dt>Venue</dt>
            <dd>{s.overview.venue || '—'}</dd>
            <dt>Time zone</dt>
            <dd>{s.overview.timeZone || '—'}</dd>
          </dl>
        </Panel>
        <Panel title="Payout account">
          {s.payout.assigned ? (
            <p className="op-p">
              <span className={`op-pill ${s.payout.payoutsEnabled ? 'open' : 'late'}`}>
                {s.payout.payoutsEnabled ? 'Payouts enabled' : 'Payouts not enabled yet'}
              </span>
              {s.payout.businessName ? <span className="op-muted"> {s.payout.businessName}</span> : null}
            </p>
          ) : (
            <p className="op-p">
              <span className="op-pill closed">No payout account assigned</span>
              <span className="op-muted"> Contact Feis.link — teachers can't pay until one is set.</span>
            </p>
          )}
          <div className="op-h3" role="heading" aria-level="3">
            Fee schedule
          </div>
          <dl className="op-dl">
            <dt>Family fee</dt>
            <dd>{fs.familyFee !== null ? money(fs.familyFee * 100, cur) : '—'}</dd>
            <dt>School assessment</dt>
            <dd>
              {fs.schoolAssessmentPerDancer !== null ? `${money(fs.schoolAssessmentPerDancer * 100, cur)} per dancer` : '—'}
            </dd>
            <dt>Late fee</dt>
            <dd>
              {fs.lateFee
                ? fs.lateFee.mode === 'percent'
                  ? `${fs.lateFee.value}% of entry fees`
                  : `${money(fs.lateFee.value * 100, cur)} per entry`
                : '—'}
            </dd>
            {(fs.addOns || []).map((a) => (
              <React.Fragment key={a.key}>
                <dt>{a.label}</dt>
                <dd>{money((a.amount || 0) * 100, cur)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </Panel>
      </div>
      <Panel title="Add-on sales">
        <GridTable
          rowKey={(r) => r.key}
          empty="No add-ons sold yet."
          rows={s.addOns}
          cols={[
            { key: 'label', label: 'Item', w: '2fr' },
            { key: 'qty', label: 'Quantity', align: 'right' },
            { key: 'amountCents', label: 'Amount', align: 'right', render: (r) => money(r.amountCents, cur) },
          ]}
        />
      </Panel>
    </>
  );
}

function Competitions({ s, eventId }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  // syllabus order (the same canonical sort the teacher screens use)
  const ordered = useMemo(() => [...s.competitions].sort(compareComps), [s.competitions]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? ordered.filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(t)) : ordered;
  }, [q, ordered]);
  if (open) return <CompetitionDetail comp={open} eventId={eventId} onBack={() => setOpen(null)} />;
  return (
    <Panel
      title={`Competitions · ${s.counts.competitionsWithEntries} of ${s.counts.competitions} with entries`}
      actions={
        <>
          <Search value={q} onChange={setQ} placeholder="Code or name" />
          <DownloadBtn onClick={() => download(eventId, 'reports/competitors.pdf', 'competitor-lists.pdf')}>
            Competitor lists (PDF)
          </DownloadBtn>
        </>
      }
    >
      <p className="op-note">Click a competition to see who's entered.</p>
      <GridTable
        rowKey={(r) => r.id}
        rows={rows}
        onRowClick={setOpen}
        cols={[
          { key: 'code', label: 'Code', w: '110px' },
          { key: 'name', label: 'Competition', w: '3fr' },
          { key: 'day', label: 'Day', w: '1fr' },
          { key: 'entries', label: 'Entries', align: 'right', render: (r) => <strong>{r.entries}</strong> },
          { key: 'go', label: '', w: '32px', align: 'right', render: () => <Msr>chevron_right</Msr> },
        ]}
      />
    </Panel>
  );
}

// One competition's entrants — like the Competitors screen, scoped to one
// competition (draw numbers once the draw is locked). Team competitions list
// teams with their members.
function CompetitionDetail({ comp, eventId, onBack }) {
  const [state, setState] = useState({ loading: true });
  const [q, setQ] = useState('');
  useEffect(() => {
    let live = true;
    Promise.all([getCompetitionList(eventId, comp.id), getCompetitors(eventId), getTeamRosters(eventId)])
      .then(([list, competitors, teams]) => live && setState({ list, competitors, teams }))
      .catch((e) => live && setState({ error: (e && e.messages) || 'Could not load this competition.' }));
    return () => {
      live = false;
    };
  }, [eventId, comp.id]);

  const isTeam = state.list && state.list.entryType === 'team';
  const hasDraw = !!(state.list && state.list.hasDraw);
  const rows = useMemo(() => {
    if (!state.list) return [];
    const byDancer = new Map(((state.competitors && state.competitors.rows) || []).map((r) => [String(r.dancer_id), r]));
    const byTeam = new Map(((state.teams && state.teams.rows) || []).map((t) => [t.id, t]));
    return state.list.rows.map((r) => {
      const d = byDancer.get(r.dancerId) || {};
      const t = byTeam.get(r.ref) || {};
      return { ...r, birthYear: d.birthYear || null, worldMedalHolder: !!d.worldMedalHolder, lname: d.lname || '', team: t };
    });
  }, [state]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? rows.filter((r) => `${r.name} ${r.school} ${r.number} ${(r.team && r.team.team) || ''}`.toLowerCase().includes(t)) : rows;
  }, [q, rows]);
  const anySet = rows.some((r) => r.setDance);
  // Registered round-dance choice ("R3: Light Jig"); the server sends it only
  // for competitions that offer one (ECRO U7 recall, Adult Championships).
  const anyRound = rows.some((r) => r.roundDance);

  const numberCol = hasDraw ? [{ key: 'number', label: '#', w: '70px', sort: (r) => Number(r.number) || 0 }] : [];
  const cols = isTeam
    ? [
        ...numberCol,
        // "Team A" per school per competition (card ac23c7e0)
        { key: 'team', label: 'Team', w: '90px', sort: (r) => r.team.team || '', render: (r) => r.team.team || '—' },
        { key: 'school', label: 'School', w: '1.4fr', sort: (r) => r.school },
        { key: 'dance', label: 'Dance', w: '1fr', render: (r) => danceLabel(r.team.dance) },
        {
          key: 'members',
          label: 'Members',
          w: '2.6fr',
          render: (r) => (
            <ul className="op-members">
              {(r.team.membersDetail || []).map((m, i) => (
                <li key={`${m.name}-${i}`}>
                  {m.name}
                  {m.birthYear ? <span className="op-muted"> ({m.birthYear})</span> : null}
                </li>
              ))}
            </ul>
          ),
        },
        {
          key: 'approval',
          label: 'Regional approval',
          w: '1.2fr',
          render: (r) =>
            !r.team.attested ? null : r.team.regionalApproved ? (
              <span className="op-pill open">Approved</span>
            ) : (
              <span className="op-pill late">Needs approval</span>
            ),
        },
      ]
    : [
        ...numberCol,
        { key: 'name', label: 'Dancer', w: '1.6fr', sort: (r) => `${r.lname} ${r.name}` },
        { key: 'school', label: 'School', w: '1.6fr', sort: (r) => r.school },
        { key: 'birthYear', label: 'Born', w: '80px', sort: (r) => r.birthYear || 0 },
        ...(anySet
          ? [
              {
                key: 'setDance',
                label: 'Set dance',
                w: '1.4fr',
                sort: (r) => r.setDance,
                render: (r) => [danceLabel(r.setDance), r.speed].filter((x) => x && x !== '—').join(' · '),
              },
            ]
          : []),
        ...(anyRound
          ? [{ key: 'roundDance', label: 'Round dance', w: '1.2fr', sort: (r) => r.roundDance, render: (r) => r.roundDance }]
          : []),
        {
          key: 'wmh',
          label: 'WMH',
          w: '90px',
          sort: (r) => (r.worldMedalHolder ? 0 : 1),
          render: (r) => (r.worldMedalHolder ? <span className="op-pill open" title="World medal holder">WMH</span> : null),
        },
      ];

  return (
    <>
      <button type="button" className="op-back" onClick={onBack}>
        <Msr>arrow_back</Msr>All competitions
      </button>
      <Panel
        title={`${comp.code} · ${comp.name} · ${rows.length} ${isTeam ? 'teams' : 'dancers'}`}
        actions={
          <>
            <Search value={q} onChange={setQ} placeholder={isTeam ? 'School, team or number' : 'Name, school or number'} />
            <DownloadBtn
              onClick={() => download(eventId, `reports/competitors?comp=${comp.id}&format=csv`, `competitors-${comp.code}.csv`)}
            >
              CSV
            </DownloadBtn>
          </>
        }
      >
        {state.loading ? (
          <div className="op-empty">Loading…</div>
        ) : state.error ? (
          <div className="op-error">{state.error}</div>
        ) : (
          <>
            <p className="op-note">
              {hasDraw ? 'In draw order, with competitor numbers.' : 'The draw has not been made yet — listed by name.'}
            </p>
            <GridTable
              rowKey={(r, i) => `${r.ref}-${i}`}
              rows={shown}
              empty={isTeam ? 'No teams entered.' : 'No dancers entered.'}
              rowClass={(r) => (isTeam && r.team.attested ? (r.team.regionalApproved ? 'op-ok' : 'op-flag') : '')}
              defaultSort={hasDraw ? { key: 'number', dir: 'asc' } : isTeam ? { key: 'school', dir: 'asc' } : { key: 'name', dir: 'asc' }}
              cols={cols}
            />
          </>
        )}
      </Panel>
    </>
  );
}

function Competitors({ data, eventId }) {
  const [q, setQ] = useState('');
  const [onlyWmh, setOnlyWmh] = useState(false);
  const all = useMemo(() => (data && data.rows) || [], [data]);
  const wmhCount = useMemo(() => all.filter((r) => r.worldMedalHolder).length, [all]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return all.filter(
      (r) =>
        (!onlyWmh || r.worldMedalHolder) &&
        (!t || `${r.name} ${r.school} ${[...r.soloComps, ...r.teamComps].join(' ')}`.toLowerCase().includes(t))
    );
  }, [q, all, onlyWmh]);
  return (
    <Panel
      title={`Competitors · ${all.length}`}
      actions={
        <>
          <button
            type="button"
            className={`op-chip${onlyWmh ? ' on' : ''}`}
            onClick={() => setOnlyWmh((v) => !v)}
            aria-pressed={onlyWmh}
          >
            <Msr>workspace_premium</Msr>World medal holders ({wmhCount})
          </button>
          <Search value={q} onChange={setQ} placeholder="Name, school or competition" />
          <DownloadBtn onClick={() => download(eventId, 'competitors?format=csv', 'competitors.csv')}>CSV</DownloadBtn>
        </>
      }
    >
      <GridTable
        rowKey={(r) => String(r.dancer_id)}
        rows={rows}
        empty={onlyWmh ? 'No world medal holders entered.' : 'No paid entries yet.'}
        defaultSort={{ key: 'name', dir: 'asc' }}
        cols={[
          { key: 'name', label: 'Dancer', w: '1.5fr', sort: (r) => `${r.lname} ${r.fname}` },
          { key: 'school', label: 'School', w: '1.5fr', sort: (r) => r.school },
          { key: 'birthYear', label: 'Born', w: '80px', sort: (r) => r.birthYear || 0 },
          {
            key: 'comps',
            label: 'Competitions',
            w: '2.2fr',
            sort: (r) => [...r.soloComps, ...r.teamComps].join(' '),
            render: (r) => [...r.soloComps, ...r.teamComps].join(', '),
          },
          {
            key: 'wmh',
            label: 'WMH',
            w: '90px',
            sort: (r) => (r.worldMedalHolder ? 0 : 1),
            render: (r) => (r.worldMedalHolder ? <span className="op-pill open" title="World medal holder">WMH</span> : null),
          },
        ]}
      />
    </Panel>
  );
}

function Teams({ data, eventId, onApproved }) {
  const [onlyNeeds, setOnlyNeeds] = useState(false);
  // saves in flight, per team: a team's button stays locked until ITS save
  // returns, so one team never gets overlapping (out-of-order) saves and
  // another team's save finishing never unlocks it
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const all = (data && data.rows) || [];
  const needs = all.filter((t) => t.attested);
  const pending = needs.filter((t) => !t.regionalApproved);
  const rows = onlyNeeds ? needs : all;
  const mark = async (team, approved) => {
    if (saving[team.id]) return;
    setSaving((x) => ({ ...x, [team.id]: true }));
    setError('');
    try {
      const r = await setTeamApproval(eventId, team.id, approved);
      onApproved(team.id, r.regionalApproved, r.regionalApprovedAt);
    } catch (e) {
      setError((e && e.messages) || 'Could not save — try again.');
    } finally {
      setSaving((x) => {
        const { [team.id]: _done, ...rest } = x;
        return rest;
      });
    }
  };
  return (
    <Panel
      title={`Teams · ${all.length}`}
      actions={
        <>
          <button
            type="button"
            className={`op-chip warn${onlyNeeds ? ' on' : ''}`}
            onClick={() => setOnlyNeeds((v) => !v)}
            aria-pressed={onlyNeeds}
          >
            <Msr>flag</Msr>Needs regional approval ({needs.length}
            {pending.length !== needs.length ? `, ${pending.length} not yet marked` : ''})
          </button>
          <DownloadBtn onClick={() => download(eventId, 'reports/teams?format=csv', 'team-rosters.csv')}>CSV</DownloadBtn>
        </>
      }
    >
      <p className="op-note">
        Teams highlighted in amber were entered outside the team rules with the teacher's statement that the regional
        director approved them. Marking one approved is your own checklist — it changes nothing else.
      </p>
      {error ? <div className="op-error">{error}</div> : null}
      <GridTable
        rowKey={(r) => r.id}
        rows={rows}
        empty={onlyNeeds ? 'No teams needed regional approval.' : 'No paid teams yet.'}
        rowClass={(r) => (r.attested ? (r.regionalApproved ? 'op-ok' : 'op-flag') : '')}
        defaultSort={{ key: 'competition', dir: 'asc' }}
        cols={[
          { key: 'competition', label: 'Comp', w: '100px', sort: (r) => r.competition },
          { key: 'school', label: 'School', w: '1.3fr', sort: (r) => r.school },
          { key: 'team', label: 'Team', w: '90px', sort: (r) => r.team, render: (r) => r.team || '—' },
          { key: 'dance', label: 'Dance', w: '1fr', sort: (r) => r.dance, render: (r) => danceLabel(r.dance) },
          {
            key: 'members',
            label: 'Members',
            w: '2.6fr',
            render: (r) => (
              <ul className="op-members">
                {(r.membersDetail || []).map((m, i) => (
                  <li key={`${m.name}-${i}`}>
                    {m.name}
                    {m.birthYear ? <span className="op-muted"> ({m.birthYear})</span> : null}
                  </li>
                ))}
              </ul>
            ),
          },
          {
            key: 'approval',
            label: 'Regional approval',
            w: '1.7fr',
            sort: (r) => (!r.attested ? 2 : r.regionalApproved ? 1 : 0),
            render: (r) =>
              !r.attested ? (
                <span className="op-muted op-small">Within the rules</span>
              ) : (
                <div className="op-appr">
                  <div className="op-small">{(r.overriddenRuleCodes || []).map(ruleLabel).join(' · ')}</div>
                  {r.regionalApproved ? (
                    <button type="button" className="op-btn sm ok" disabled={!!saving[r.id]} onClick={() => mark(r, false)} title="Clear the mark">
                      <Msr>check_circle</Msr>Approved
                    </button>
                  ) : (
                    <button type="button" className="op-btn sm" disabled={!!saving[r.id]} onClick={() => mark(r, true)}>
                      <Msr>task_alt</Msr>Mark approved
                    </button>
                  )}
                </div>
              ),
          },
        ]}
      />
    </Panel>
  );
}

function Schools({ s }) {
  const cur = s.overview.currency;
  return (
    <Panel title={`Schools · ${s.schools.length}`}>
      <GridTable
        rowKey={(r, i) => `${r.email}-${i}`}
        rows={s.schools}
        empty="No schools have entered yet."
        defaultSort={{ key: 'school', dir: 'asc' }}
        cols={[
          { key: 'school', label: 'School', w: '1.8fr', sort: (r) => r.school, render: (r) => r.school || '—' },
          {
            key: 'teacher',
            label: 'Teacher',
            w: '1.8fr',
            sort: (r) => r.teacher,
            render: (r) => (
              <>
                {r.teacher}
                <Emails value={r.email} />
              </>
            ),
          },
          { key: 'dancers', label: 'Dancers', align: 'right', sort: (r) => r.dancers },
          { key: 'soloEntries', label: 'Solo', align: 'right', sort: (r) => r.soloEntries },
          { key: 'teams', label: 'Teams', align: 'right', sort: (r) => r.teams },
          { key: 'paidCents', label: 'Paid', align: 'right', sort: (r) => r.paidCents, render: (r) => money(r.paidCents, cur) },
          {
            key: 'balanceCents',
            label: 'Owing',
            align: 'right',
            sort: (r) => r.balanceCents,
            render: (r) => (r.balanceCents ? money(r.balanceCents, cur) : '—'),
          },
        ]}
      />
    </Panel>
  );
}

// The organizer's money only: the server already strips the Feis.link
// service fee from every amount it sends them. Stats on top, then one table
// at a time behind a secondary tab bar, each with its own CSV (Devin 2026-10-17).
const MONEY_VIEWS = [
  { key: 'family', label: 'Family fees', icon: 'diversity_3' },
  { key: 'plans', label: 'Instalment plans', icon: 'event_repeat' },
  { key: 'tx', label: 'Transactions', icon: 'receipt_long' },
];

function Money({ s, tx, eventId }) {
  const cur = s.overview.currency;
  const plans = s.instalments || [];
  // Family fees for the treasurer: collected = on fully paid orders; fees on
  // an instalment order still owing are shown apart, never split.
  const ff = s.familyFees;
  const ffRows = (s.schools || []).filter((r) => r.familyFeeCents || r.familyFeePendingCents || r.familyFeeWaived);
  const ffPending = !!(ff && ff.pendingCents);
  const views = MONEY_VIEWS.filter((v) => v.key !== 'family' || ff);
  const [view, setView] = useState(views[0].key);
  const fileName = (what) => `${(s.overview.name || 'oireachtas').replace(/\s+/g, '-')}-${what}.csv`;

  const familyCsv = () =>
    downloadCsv(
      fileName('family-fees'),
      tableCsv(
        ['School', 'Teacher', 'Families', 'Waived', 'Collected', ...(ffPending ? ['On unpaid balance'] : [])],
        [
          ...ffRows.map((r) => [
            r.school || '—',
            r.teacher || '',
            r.familyFeeFamilies,
            r.familyFeeWaived,
            csvMoney(r.familyFeeCents),
            ...(ffPending ? [csvMoney(r.familyFeePendingCents)] : []),
          ]),
          ['Total', '', ff.families, ff.waived, csvMoney(ff.collectedCents), ...(ffPending ? [csvMoney(ff.pendingCents)] : [])],
        ]
      )
    );
  const plansCsv = () =>
    downloadCsv(
      fileName('instalment-plans'),
      tableCsv(
        ['School', 'Teacher', 'Status', 'Order', 'Paid', 'Balance', 'Due'],
        plans.map((r) => [
          r.school || '—',
          r.teacher || '',
          r.status,
          csvMoney(r.totalCents),
          csvMoney(r.paidCents),
          csvMoney(r.balanceCents),
          r.balanceDueAt ? formatDate(r.balanceDueAt).toLocaleDateString('en-CA') : '', // YYYY-MM-DD for spreadsheets
        ])
      )
    );

  return (
    <>
      <div className="op-stats">
        <Stat label="Collected for your event" value={money(s.money.collectedCents, cur)} sub={`${s.money.orders} paid orders`} />
        <Stat label="Still owed (instalments)" value={money(s.money.outstandingCents, cur)} />
        {ff && (
          <Stat
            label="Family fees collected"
            value={money(ff.collectedCents, cur)}
            sub={`${ff.families} ${ff.families === 1 ? 'family' : 'families'}${ff.waived ? ` · ${ff.waived} waived` : ''}`}
          />
        )}
      </div>
      <div className="ce-views" role="group" aria-label="Money tables">
        {views.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`vt${view === v.key ? ' on' : ''}`}
            aria-pressed={view === v.key}
            onClick={() => setView(v.key)}
          >
            <Msr>{v.icon}</Msr>
            {v.label}
          </button>
        ))}
      </div>
      {view === 'family' && ff && (
        <Panel
          title={`Family fees by school · ${ffRows.length}`}
          actions={<DownloadBtn onClick={familyCsv}>CSV</DownloadBtn>}
        >
          {ffPending && (
            <p className="op-note">
              {money(ff.pendingCents, cur)} more in family fees ({ff.pendingFamilies}{' '}
              {ff.pendingFamilies === 1 ? 'family' : 'families'}) is on instalment orders still being paid — counted once the
              balance is paid.
            </p>
          )}
          <GridTable
            rowKey={(r, i) => `${r.school || r.teacher}-${i}`}
            rows={ffRows}
            empty="No family fees collected yet."
            defaultSort={{ key: 'school', dir: 'asc' }}
            footer={{
              school: 'Total',
              familyFeeFamilies: <strong>{ff.families}</strong>,
              familyFeeWaived: <strong>{ff.waived}</strong>,
              familyFeeCents: <strong>{money(ff.collectedCents, cur)}</strong>,
              ...(ffPending ? { familyFeePendingCents: <strong>{money(ff.pendingCents, cur)}</strong> } : {}),
            }}
            cols={[
              { key: 'school', label: 'School', w: '2fr', sort: (r) => r.school || r.teacher, render: (r) => r.school || r.teacher || '—' },
              { key: 'familyFeeFamilies', label: 'Families', align: 'right', sort: (r) => r.familyFeeFamilies },
              { key: 'familyFeeWaived', label: 'Waived', align: 'right', sort: (r) => r.familyFeeWaived },
              { key: 'familyFeeCents', label: 'Collected', align: 'right', sort: (r) => r.familyFeeCents, render: (r) => money(r.familyFeeCents, cur) },
              ...(ffPending
                ? [
                    {
                      key: 'familyFeePendingCents',
                      label: 'On unpaid balance',
                      align: 'right',
                      sort: (r) => r.familyFeePendingCents,
                      render: (r) => (r.familyFeePendingCents ? money(r.familyFeePendingCents, cur) : '—'),
                    },
                  ]
                : []),
            ]}
          />
        </Panel>
      )}
      {view === 'plans' && (
        <Panel
          title={`Instalment plans · ${plans.length}`}
          actions={plans.length ? <DownloadBtn onClick={plansCsv}>CSV</DownloadBtn> : null}
        >
          <GridTable
            rows={plans}
            empty="No instalment plans started yet."
            cols={[
              { key: 'school', label: 'School', w: '1.6fr', render: (r) => r.school || '—' },
              { key: 'teacher', label: 'Teacher', w: '1.4fr' },
              { key: 'status', label: 'Status', w: '1fr', render: (r) => <span className="op-pill cap">{r.status}</span> },
              { key: 'totalCents', label: 'Order', align: 'right', render: (r) => money(r.totalCents, cur) },
              { key: 'paidCents', label: 'Paid', align: 'right', render: (r) => money(r.paidCents, cur) },
              { key: 'balanceCents', label: 'Balance', align: 'right', render: (r) => money(r.balanceCents, cur) },
              { key: 'balanceDueAt', label: 'Due', w: '1.3fr', render: (r) => day(r.balanceDueAt) },
            ]}
          />
        </Panel>
      )}
      {view === 'tx' && (
        <Panel
          title={`Transactions · ${(tx && tx.rows && tx.rows.length) || 0}`}
          actions={
            <DownloadBtn onClick={() => download(eventId, 'reports/transactions?format=csv', 'transactions.csv')}>
              CSV
            </DownloadBtn>
          }
        >
          <GridTable
            rows={(tx && tx.rows) || []}
            empty="No payments yet."
            defaultSort={{ key: 'date', dir: 'desc' }}
            cols={[
              { key: 'receipt', label: 'Receipt', w: '90px', sort: (r) => Number(r.receipt) || 0 },
              { key: 'date', label: 'Date', w: '110px', sort: (r) => r.date },
              { key: 'teacher', label: 'Teacher', w: '1.6fr', sort: (r) => r.teacher },
              { key: 'entries', label: 'Dancers', align: 'right' },
              { key: 'teams', label: 'Teams', align: 'right' },
              { key: 'order', label: 'Order', align: 'right', render: (r) => moneyStr(r.order, cur) },
              { key: 'paid', label: 'Paid', align: 'right', render: (r) => moneyStr(r.paid, cur) },
              {
                key: 'status',
                label: 'Status',
                w: '120px',
                render: (r) => <span className={`op-pill cap ${r.status === 'paid' ? 'open' : 'late'}`}>{r.status}</span>,
              },
            ]}
          />
        </Panel>
      )}
    </>
  );
}

// Add-on count sheet: what each school bought (tickets, ads …) so the
// organizer knows what to hand each school at the event, with category totals.
function downloadCountSheet(eventName, items, rows) {
  downloadCsv(`${(eventName || 'oireachtas').replace(/\s+/g, '-')}-add-on-count-sheet.csv`, countSheetCsv(items, rows));
}

// Save CSV text built in the browser as a file.
function downloadCsv(fileName, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Assessments({ s, recon, eventId }) {
  const cur = s.overview.currency;
  const items = s.addOns || [];
  const bySchool = s.addOnsBySchool || [];
  const t = (recon && recon.totals) || {};
  const footer = { school: 'Total' };
  for (const i of items) footer[i.key] = <strong>{i.qty}</strong>;
  footer.amount = <strong>{money(items.reduce((n, i) => n + i.amountCents, 0), cur)}</strong>;
  return (
    <>
      <div className="op-stats">
        {items.length ? (
          items.map((i) => <Stat key={i.key} label={i.label} value={i.qty} sub={money(i.amountCents, cur)} />)
        ) : (
          <Stat label="Add-ons" value="0" sub="none chosen yet" />
        )}
      </div>
      <Panel
        title={`Add-ons by school · ${bySchool.length}`}
        actions={
          items.length ? (
            <DownloadBtn onClick={() => downloadCountSheet(s.overview.name, items, bySchool)}>Count sheet (CSV)</DownloadBtn>
          ) : null
        }
      >
        <p className="op-note">What each school chose with its assessment — hand these out at the event.</p>
        <GridTable
          rowKey={(r, i) => `${r.school}-${i}`}
          rows={bySchool}
          empty="No add-ons chosen yet."
          defaultSort={{ key: 'school', dir: 'asc' }}
          footer={footer}
          cols={[
            { key: 'school', label: 'School', w: '2fr', sort: (r) => r.school, render: (r) => r.school || '—' },
            ...items.map((i) => ({
              key: i.key,
              label: i.label,
              align: 'right',
              sort: (r) => r.qty[i.key] || 0,
              render: (r) => r.qty[i.key] || '—',
            })),
            { key: 'amount', label: 'Value', align: 'right', sort: (r) => r.amountCents, render: (r) => money(r.amountCents, cur) },
          ]}
        />
      </Panel>
      <Panel
        title="Assessment money by school"
        actions={
          <DownloadBtn onClick={() => download(eventId, 'reports/reconciliation?format=csv', 'assessments.csv')}>CSV</DownloadBtn>
        }
      >
        <GridTable
          rows={(recon && recon.rows) || []}
          empty="No school assessments yet."
          defaultSort={{ key: 'school', dir: 'asc' }}
          footer={{
            school: 'Total',
            pool: <strong>{moneyStr(t.pool, cur)}</strong>,
            allocated: <strong>{moneyStr(t.allocated, cur)}</strong>,
            sponsorship: <strong>{moneyStr(t.sponsorship, cur)}</strong>,
            overageCollected: <strong>{moneyStr(t.overageCollected, cur)}</strong>,
          }}
          cols={[
            { key: 'school', label: 'School', w: '2fr', sort: (r) => r.school },
            { key: 'pool', label: 'Assessment pool', align: 'right', sort: (r) => Number(r.pool), render: (r) => moneyStr(r.pool, cur) },
            { key: 'allocated', label: 'Spent on add-ons', align: 'right', sort: (r) => Number(r.allocated), render: (r) => moneyStr(r.allocated, cur) },
            { key: 'sponsorship', label: 'Sponsorship', align: 'right', sort: (r) => Number(r.sponsorship), render: (r) => moneyStr(r.sponsorship, cur) },
            { key: 'overageCollected', label: 'Paid over pool', align: 'right', sort: (r) => Number(r.overageCollected), render: (r) => moneyStr(r.overageCollected, cur) },
          ]}
        />
      </Panel>
    </>
  );
}

function LateAndUnfinished({ s }) {
  const cur = s.overview.currency;
  const tz = s.overview.timeZone;
  return (
    <>
      <Panel title={`Late entries · ${s.lateEntries.length}`}>
        <GridTable
          rows={s.lateEntries}
          empty="No late entries."
          cols={[
            { key: 'receipt', label: 'Receipt', w: '90px' },
            { key: 'paidAt', label: 'Paid', w: '1.2fr', render: (r) => when(r.paidAt, tz) },
            { key: 'school', label: 'School', w: '1.6fr', render: (r) => r.school || '—' },
            { key: 'teacher', label: 'Teacher', w: '1.4fr' },
            { key: 'entries', label: 'Dancers', align: 'right' },
            { key: 'lateFeeCents', label: 'Late fee', align: 'right', render: (r) => money(r.lateFeeCents, cur) },
          ]}
        />
      </Panel>
      <Panel title={`Unfinished entries · ${s.unfinished.length}`}>
        <p className="op-note">Schools with a saved draft that hasn't been submitted and paid.</p>
        <GridTable
          rows={s.unfinished}
          empty="No unfinished drafts."
          cols={[
            { key: 'school', label: 'School', w: '1.6fr', render: (r) => r.school || '—' },
            {
              key: 'teacher',
              label: 'Teacher',
              w: '1.8fr',
              render: (r) => (
                <>
                  {r.teacher}
                  <Emails value={r.email} />
                </>
              ),
            },
            { key: 'dancers', label: 'Dancers', align: 'right' },
            { key: 'teams', label: 'Teams', align: 'right' },
            { key: 'updatedAt', label: 'Last saved', w: '1.2fr', render: (r) => when(r.updatedAt, tz) },
          ]}
        />
      </Panel>
    </>
  );
}

// ---- page -------------------------------------------------------------------
const LOADERS = {
  competitors: getCompetitors,
  teams: getTeamRosters,
  money: getTransactions,
  assessments: getReconciliation,
};

// Keyed by event id: switching events remounts the panel, so no report data,
// error, drill-in or filter from one event can show under another's heading.
const OrganizerOireachtas = () => {
  const { id } = useParams();
  return <OrganizerPanel key={id} id={id} />;
};

function ReportError({ message, onRetry }) {
  return (
    <div className="op-error" role="alert">
      {message}{' '}
      <button type="button" className="op-btn sm" onClick={onRetry}>
        <Msr>refresh</Msr>Try again
      </button>
    </div>
  );
}

function OrganizerPanel({ id }) {
  useMaterialSymbols();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  // per report tab: { status: 'loading' | 'ok' | 'error', data, error }
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // set in setup AND cleared in cleanup, so StrictMode's effect replay
  // (setup -> cleanup -> setup) leaves it true
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadSummary = useCallback(() => {
    setLoading(true);
    setError('');
    getOrganizerSummary(id)
      .then((s) => mounted.current && setSummary(s))
      .catch((e) => mounted.current && setError((e && e.messages) || 'Could not load this event.'))
      .finally(() => mounted.current && setLoading(false));
  }, [id]);
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // A report request always lands in its tab's slot, even if the organizer has
  // moved to another tab meanwhile (nothing is left stranded on "Loading…").
  const loadReport = useCallback(
    (key) => {
      const loader = LOADERS[key];
      if (!loader) return;
      setReports((x) => ({ ...x, [key]: { status: 'loading' } }));
      loader(id)
        .then((data) => mounted.current && setReports((x) => ({ ...x, [key]: { status: 'ok', data } })))
        .catch(
          (e) =>
            mounted.current &&
            setReports((x) => ({
              ...x,
              [key]: { status: 'error', error: (e && e.messages) || 'Could not load this report.' },
            }))
        );
    },
    [id]
  );
  useEffect(() => {
    if (LOADERS[tab] && !reports[tab]) loadReport(tab);
  }, [tab, reports, loadReport]);

  // keep the Teams tab in step after a mark is saved
  const onApproved = (teamId, regionalApproved, regionalApprovedAt) =>
    setReports((x) => {
      const t = x.teams;
      if (!t || t.status !== 'ok') return x;
      const rows = ((t.data && t.data.rows) || []).map((r) =>
        r.id === teamId ? { ...r, regionalApproved, regionalApprovedAt } : r
      );
      return { ...x, teams: { ...t, data: { ...t.data, rows } } };
    });

  const body = () => {
    if (!summary) return null;
    const r = reports[tab];
    if (LOADERS[tab]) {
      if (!r || r.status === 'loading') return <div className="op-empty">Loading…</div>;
      if (r.status === 'error') return <ReportError message={r.error} onRetry={() => loadReport(tab)} />;
    }
    const d = r && r.data;
    switch (tab) {
      case 'status':
        return <OrganizerStatus eventId={id} />;
      case 'competitions':
        return <Competitions s={summary} eventId={id} />;
      case 'competitors':
        return <Competitors data={d} eventId={id} />;
      case 'teams':
        return <Teams data={d} eventId={id} onApproved={onApproved} />;
      case 'schools':
        return <Schools s={summary} />;
      case 'money':
        return <Money s={summary} tx={d} eventId={id} />;
      case 'assessments':
        return <Assessments s={summary} recon={d} eventId={id} />;
      case 'entries':
        return <LateAndUnfinished s={summary} />;
      default:
        return <Overview s={summary} />;
    }
  };

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page">
        {loading && <Loader />}
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate('/organizer-dashboard')}>
            <Msr>arrow_back</Msr>Dashboard
          </button>
          {error ? (
            <ReportError message={error} onRetry={loadSummary} />
          ) : summary ? (
            <>
              <div className="ph">
                <div>
                  <h1>{summary.overview.name}</h1>
                  <div className="sub">
                    <span>{summary.overview.region}</span>
                    <span className={`op-pill ${summary.overview.registration.state}`}>
                      {REG_STATE[summary.overview.registration.state]}
                    </span>
                    <span className="op-muted">View only — changes go through Feis.link.</span>
                  </div>
                </div>
                {/* the one thing organizers edit here (Devin 2026-10-07, card 524449bc) */}
                <button type="button" className="op-btn op-ph-act" onClick={() => navigate(`/organizer-oireachtas/${id}/schedule`)}>
                  <Msr>view_week</Msr>Stage schedule
                </button>
              </div>
              <div className="ce-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    className={`ce-tab${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    <Msr>{t.icon}</Msr>
                    {t.label}
                  </button>
                ))}
              </div>
              {body()}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default OrganizerOireachtas;
